(() => {
  if (window.__zProductCardImageSwapLoaded) return;
  window.__zProductCardImageSwapLoaded = true;

  const WRAPPER = '[data-z-product-image-swap]';
  const IMAGE_LINK = `${WRAPPER} > a`;
  const SECOND_IMAGE = '.product-card__image--second img';
  const READY = 'is-z-secondary-ready';
  const TOUCH_PREVIEW = 'is-z-touch-preview';

  const HOLD_MS = 80;
  const MOVE_PX = 12;
  const PREVIEW_AFTER_RELEASE_MS = 900;

  const loadStates = new WeakMap();
  let press = null;
  let previewTimeout = null;
  let suppressClickFor = null;
  let suppressClickTimeout = null;

  const element = (target) =>
    target instanceof Element ? target : null;

  const wrapperFrom = (target) => {
    const link = element(target)?.closest(IMAGE_LINK);
    return link?.closest(WRAPPER) || null;
  };

  const secondImageFrom = (wrapper) =>
    wrapper?.querySelector(SECOND_IMAGE) || null;

  const closePreviews = (except = null) => {
    document
      .querySelectorAll(`${WRAPPER}.${TOUCH_PREVIEW}`)
      .forEach((wrapper) => {
        if (wrapper !== except) {
          wrapper.classList.remove(TOUCH_PREVIEW);
        }
      });
  };

  const loadSecondImage = (wrapper) => {
    const image = secondImageFrom(wrapper);

    if (!image) {
      wrapper?.classList.remove(READY);
      return Promise.resolve(false);
    }

    const sourceKey =
      image.currentSrc ||
      image.getAttribute('srcset') ||
      image.getAttribute('src') ||
      '';

    const oldState = loadStates.get(image);

    if (oldState?.sourceKey === sourceKey) {
      return oldState.promise;
    }

    wrapper.classList.remove(READY);
    image.loading = 'eager';

    const promise = new Promise((resolve) => {
      const finish = async (loaded) => {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);

        if (!loaded || image.naturalWidth < 1) {
          loadStates.delete(image);
          wrapper.classList.remove(READY);
          resolve(false);
          return;
        }

        try {
          if (typeof image.decode === 'function') {
            await image.decode();
          }
        } catch (error) {
          // The loaded image can normally still be displayed.
        }

        if (
          secondImageFrom(wrapper) === image &&
          image.naturalWidth > 0
        ) {
          wrapper.classList.add(READY);
          resolve(true);
        } else {
          loadStates.delete(image);
          wrapper.classList.remove(READY);
          resolve(false);
        }
      };

      const onLoad = () => finish(true);
      const onError = () => finish(false);

      if (image.complete) {
        finish(image.naturalWidth > 0);
        return;
      }

      image.addEventListener('load', onLoad, {
        once: true
      });

      image.addEventListener('error', onError, {
        once: true
      });
    });

    loadStates.set(image, {
      sourceKey,
      promise
    });

    return promise;
  };

  const cancelPress = () => {
    if (!press) return;

    window.clearTimeout(press.timer);
    press.wrapper.classList.remove(TOUCH_PREVIEW);
    press = null;
  };

  /*
   * Desktop:
   * Prepare the second image when the pointer reaches the image link.
   */
  document.addEventListener(
    'pointerover',
    (event) => {
      const wrapper = wrapperFrom(event.target);

      if (wrapper) {
        loadSecondImage(wrapper);
      }
    },
    {
      passive: true
    }
  );

  /*
   * Mobile:
   * A normal tap remains a normal product-page tap.
   * Only a deliberate press and hold starts the preview.
   */
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (
        event.pointerType !== 'touch' ||
        event.isPrimary === false
      ) {
        return;
      }

      const wrapper = wrapperFrom(event.target);

      if (!wrapper) {
        cancelPress();
        closePreviews();
        return;
      }

      if (previewTimeout) {
        window.clearTimeout(previewTimeout);
        previewTimeout = null;
      }

      cancelPress();
      closePreviews(wrapper);

      const currentPress = {
        pointerId: event.pointerId,
        wrapper,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        active: false,
        timer: 0
      };

      press = currentPress;

      const imageReady =
        loadSecondImage(wrapper);

      currentPress.timer =
        window.setTimeout(async () => {
          const ready = await imageReady;

          if (
            press !== currentPress ||
            currentPress.moved ||
            !ready
          ) {
            return;
          }

          currentPress.active = true;
          wrapper.classList.add(TOUCH_PREVIEW);
        }, HOLD_MS);
    },
    {
      passive: true
    }
  );

  /*
   * Finger movement means scrolling or swiping.
   * The preview is cancelled without blocking that movement.
   */
  document.addEventListener(
    'pointermove',
    (event) => {
      if (
        !press ||
        event.pointerId !== press.pointerId
      ) {
        return;
      }

      const movedX =
        Math.abs(event.clientX - press.startX);

      const movedY =
        Math.abs(event.clientY - press.startY);

      if (
        movedX > MOVE_PX ||
        movedY > MOVE_PX
      ) {
        press.moved = true;
        cancelPress();
      }
    },
    {
      passive: true
    }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (
        !press ||
        event.pointerId !== press.pointerId
      ) {
        return;
      }

      const finishedPress = press;

      window.clearTimeout(
        finishedPress.timer
      );

      press = null;

      if (!finishedPress.active) return;

      /*
       * Stop only the click produced by this completed long press.
       * Ordinary quick taps are not stopped.
       */
      suppressClickFor =
        finishedPress.wrapper;

      if (suppressClickTimeout) {
        window.clearTimeout(
          suppressClickTimeout
        );
      }

      suppressClickTimeout =
        window.setTimeout(() => {
          suppressClickFor = null;
        }, 700);

      previewTimeout =
        window.setTimeout(() => {
          finishedPress.wrapper.classList.remove(
            TOUCH_PREVIEW
          );

          previewTimeout = null;
        }, PREVIEW_AFTER_RELEASE_MS);
    },
    {
      passive: true
    }
  );

  document.addEventListener(
    'pointercancel',
    (event) => {
      if (
        press &&
        event.pointerId === press.pointerId
      ) {
        cancelPress();
      }
    },
    {
      passive: true
    }
  );

  document.addEventListener(
    'click',
    (event) => {
      const wrapper =
        wrapperFrom(event.target);

      if (
        !wrapper ||
        wrapper !== suppressClickFor
      ) {
        return;
      }

      suppressClickFor = null;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    },
    true
  );

  /*
   * Prevent the browser's image menu only while
   * an intentional mobile preview is active.
   */
  document.addEventListener(
    'contextmenu',
    (event) => {
      const wrapper =
        wrapperFrom(event.target);

      if (
        wrapper?.classList.contains(
          TOUCH_PREVIEW
        )
      ) {
        event.preventDefault();
      }
    }
  );

  /*
   * Document-level delegation means newly rendered cards
   * work automatically. This clears any open preview when
   * Shopify reloads a section in the theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {
      cancelPress();
      closePreviews();
      suppressClickFor = null;
    }
  );
})();